<template>
  <div class="year-wise-result-page">
    <b-card class="planner-list-card">
      <div class="gradient-line-wrapper"></div>

      <div
        class="table-header d-flex flex-wrap justify-content-between align-items-center mb-2"
      >
        <div>
          <h5 class="mb-0 resource-title">{{ $t("Yearly SSC Result") }}</h5>
          <small class="text-muted">
            {{ $t("Manage annual board / result summaries by grade") }}
          </small>
        </div>

        <div class="d-flex align-items-center flex-wrap" style="gap: 0.5rem">
          <b-button
            :variant="viewMode === 'list' ? 'primary' : 'outline-primary'"
            class="view-mode-btn"
            @click="switchView('list')"
          >
            <feather-icon icon="ListIcon" class="mr-50" />
            {{ $t("List") }}
          </b-button>
          <b-button
            :variant="viewMode === 'report' ? 'primary' : 'outline-primary'"
            class="view-mode-btn"
            @click="switchView('report')"
          >
            <feather-icon icon="BarChart2Icon" class="mr-50" />
            {{ $t("Report") }}
          </b-button>
        </div>
      </div>

      <!-- LIST VIEW -->
      <template v-if="viewMode === 'list'">
        <div
          class="pagination-controls-top d-flex justify-content-between align-items-center mb-2"
        >
          <div class="d-flex align-items-center gap-2 flex-wrap">
            <div class="page-size-wrapper">
              <label class="page-size-label">{{ $t("Show") }}:</label>
              <v-select
                v-model="perPage"
                :options="pageSizeOptions"
                :reduce="(option) => option.value"
                label="label"
                :clearable="false"
                :searchable="false"
                append-to-body
                class="page-size-select-compact"
                @input="onPageSizeChange"
              />
            </div>
          </div>

          <div class="pagination-count-display">
            <span class="count-text">
              {{ $t("Showing") }}
              <strong>{{ displayedRange.start }}</strong>
              {{ $t("to") }}
              <strong>{{ displayedRange.end }}</strong>
              {{ $t("of") }}
              <strong>{{ totalCount }}</strong>
            </span>
          </div>

          <div class="pagination-wrapper-compact">
            <b-pagination
              v-model="currentPage"
              :total-rows="totalCount"
              :per-page="perPage"
              size="sm"
              align="center"
              class="pagination-compact mb-0"
              @input="loadList"
            />
          </div>
        </div>

        <b-row class="align-items-center filter-row mb-2">
          <b-col cols="12" class="filter-toolbar d-flex align-items-center">
            <div
              class="filter-fields-group filter-fields-group--cols-3 d-flex align-items-center"
            >
              <b-form-input
                v-model="searchQuery"
                class="filter-search-input"
                :placeholder="$t('Search year') + '...'"
                @input="onSearchChange"
              />
              <v-select
                v-model="statusFilter"
                :options="statusOptions"
                :clearable="false"
                :reduce="(o) => o.value"
                label="text"
                :searchable="false"
                append-to-body
                class="filter-control filter-field-item"
                @input="onFilterChange"
              />
            </div>

            <div class="filter-actions-group d-flex align-items-center ml-auto">
              <b-button
                v-if="rights.add"
                variant="gradient-primary"
                class="action-btn-filter add-sheet-btn text-nowrap"
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                @click="openAddModal"
              >
                <feather-icon icon="PlusIcon" class="mr-50" />
                <span>{{ $t("Add Result") }}</span>
              </b-button>
            </div>
          </b-col>
        </b-row>

        <div class="table-scroll-container">
          <b-table
            class="planner-list-table mt-1"
            :items="rows"
            :fields="tableFields"
            :busy="dataLoading"
            show-empty
            :responsive="false"
            hover
            small
          >
            <template #empty>
              <h3 v-if="!dataLoading" class="mt-1 mb-1 text-center">
                {{ $t("No records found") }}
              </h3>
            </template>

            <template #table-busy>
              <div class="list-skeleton">
                <div
                  v-for="n in 6"
                  :key="'skeleton-' + n"
                  class="list-skeleton__row"
                  :style="skeletonGridStyle(tableFields.length)"
                >
                  <div
                    v-for="(field, index) in tableFields"
                    :key="'skeleton-cell-' + n + '-' + index"
                    class="list-skeleton__cell"
                  >
                    <div
                      class="list-skeleton__bar"
                      :style="{ width: skeletonBarWidth(index) }"
                    ></div>
                  </div>
                </div>
              </div>
            </template>

            <template #cell(index)="data">
              {{ (currentPage - 1) * perPage + data.index + 1 }}
            </template>

            <template #cell(year)="{ item }">
              <span class="year-chip">{{ item.year || "—" }}</span>
            </template>

            <template #cell(noOfStudents)="{ item }">
              <b-badge variant="light-primary">
                {{ numOrZero(item.noOfStudents) }}
              </b-badge>
            </template>

            <template #cell(absent)="{ item }">
              <b-badge variant="light-warning">
                {{ numOrZero(item.absent) }}
              </b-badge>
            </template>

            <template #cell(grades)="{ item }">
              <div class="grades-cell">
                <span
                  v-for="g in sortedGrades(item.grades)"
                  :key="g.id || g.type + '-' + g.sortIndex"
                  class="grade-pill"
                  :title="g.type + ': ' + numOrZero(g.count)"
                >
                  <strong>{{ g.type }}</strong>
                  <span>{{ numOrZero(g.count) }}</span>
                </span>
                <span
                  v-if="!item.grades || !item.grades.length"
                  class="text-muted"
                >
                  —
                </span>
              </div>
            </template>

            <template #cell(note)="{ item }">
              <span
                class="text-truncate d-inline-block note-cell"
                :title="item.note || ''"
              >
                {{ item.note || "—" }}
              </span>
            </template>

            <template #cell(isActive)="{ item }">
              <b-form-checkbox
                v-if="rights.edit"
                :checked="!!item.isActive"
                switch
                class="mb-0"
                @change="toggleStatus(item, $event)"
              />
              <b-badge
                v-else
                :variant="item.isActive ? 'light-success' : 'light-secondary'"
              >
                {{ item.isActive ? $t("Active") : $t("Inactive") }}
              </b-badge>
            </template>

            <template #head(actions)="data">
              <div class="text-center">
                <span>{{ data.label }}</span>
              </div>
            </template>

            <template #cell(actions)="{ item }">
              <div class="d-flex justify-content-center">
                <b-button
                  v-if="rights.edit"
                  variant="gradient-primary"
                  v-b-tooltip.hover.top
                  :title="$t('Edit')"
                  class="action-btn-table mr-1"
                  @click="editItem(item)"
                >
                  <feather-icon icon="EditIcon" />
                </b-button>
                <b-button
                  v-if="rights.delete"
                  variant="outline-danger"
                  v-b-tooltip.hover.top
                  :title="$t('Delete')"
                  class="action-btn-outline delete-btn"
                  @click="deleteItem(item)"
                >
                  <feather-icon icon="Trash2Icon" />
                </b-button>
              </div>
            </template>
          </b-table>
        </div>
      </template>

      <!-- REPORT VIEW -->
      <template v-else>
        <div class="ywr-report-toolbar no-print mb-2">
          <div class="d-flex align-items-center flex-wrap" style="gap: 0.75rem">
            <b-form-checkbox
              v-model="includeInactive"
              switch
              class="mb-0"
              @change="loadReport"
            >
              {{ $t("Include inactive") }}
            </b-form-checkbox>
          </div>

          <div class="d-flex align-items-center flex-wrap" style="gap: 0.5rem">
            <b-button
              variant="outline-primary"
              class="action-btn-filter"
              :disabled="reportLoading"
              @click="loadReport"
            >
              <b-spinner v-if="reportLoading" small class="mr-50" />
              <feather-icon v-else icon="RefreshCwIcon" class="mr-50" />
              {{ $t("Refresh") }}
            </b-button>
            <b-button
              variant="gradient-primary"
              class="action-btn-filter add-sheet-btn text-nowrap"
              :disabled="reportLoading || !sortedReportRows.length"
              @click="printReport"
            >
              <feather-icon icon="PrinterIcon" class="mr-50" />
              {{ $t("Print") }}
            </b-button>
          </div>
        </div>

        <div v-if="reportLoading" class="text-center py-5 no-print">
          <b-spinner variant="primary" style="width: 2.5rem; height: 2.5rem" />
          <p class="mt-2 text-muted mb-0">{{ $t("Loading report") }}...</p>
        </div>

        <div
          v-else-if="!sortedReportRows.length"
          class="text-center py-5 no-print"
        >
          <h3 class="mb-1">{{ $t("No records found") }}</h3>
          <p class="text-muted mb-0">
            {{ $t("Add year-wise results to generate this report") }}
          </p>
        </div>

        <div v-else class="ywr-report-sheet" id="ywr-report-print-area">
          <div class="ywr-report-header">
            <div class="ywr-school-name">{{ reportSchoolName }}</div>
            <div class="ywr-management-line">{{ reportManagementLine }}</div>
            <div class="ywr-report-title">
              Year-wise SSC Examination Result Analysis of
              {{ reportSchoolName }}
            </div>
          </div>

          <table class="ywr-analysis-table">
            <thead>
              <tr>
                <th>Year</th>
                <th>No of Student</th>
                <th
                  v-for="gradeType in reportGradeColumns"
                  :key="'th-' + gradeType"
                >
                  {{ gradeType }}
                </th>
                <th>Abs</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(item, idx) in sortedReportRows"
                :key="'ywr-row-' + (item.id || idx)"
              >
                <td>{{ item.year || "-" }}</td>
                <td>{{ displayReportNumber(item.noOfStudents) }}</td>
                <td
                  v-for="gradeType in reportGradeColumns"
                  :key="'td-' + (item.id || idx) + '-' + gradeType"
                >
                  {{ displayGradeCount(item, gradeType) }}
                </td>
                <td>
                  {{
                    displayReportNumber(
                      item.abs != null ? item.abs : item.absent
                    )
                  }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </b-card>

    <!-- Add / Edit Modal -->
    <b-modal
      v-model="showModal"
      :title="isEdit ? $t('Edit Yearly SSC Result') : $t('Add Yearly SSC Result')"
      size="lg"
      centered
      no-close-on-backdrop
      @hidden="resetForm"
      modal-class="year-wise-result-modal"
    >
      <b-form @submit.prevent>
        <b-row>
          <b-col md="6">
            <b-form-group :label="$t('Year')" label-for="ywr-year">
              <b-form-input
                id="ywr-year"
                v-model.trim="form.year"
                placeholder="e.g. 2021-2022"
              />
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group :label="$t('No. of Students')" label-for="ywr-students">
              <b-form-input
                id="ywr-students"
                :value="computedStudentTotal"
                type="number"
                min="0"
                disabled
              />
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group :label="$t('Absent')" label-for="ywr-absent">
              <b-form-input
                id="ywr-absent"
                v-model.number="form.absent"
                type="number"
                min="0"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="9">
            <b-form-group :label="$t('Note')" label-for="ywr-note">
              <b-form-input
                id="ywr-note"
                v-model.trim="form.note"
                :placeholder="$t('Optional note')"
              />
            </b-form-group>
          </b-col>
          <b-col md="3" class="d-flex align-items-center">
            <b-form-checkbox v-model="form.isActive" switch class="mt-1">
              {{ $t("Active") }}
            </b-form-checkbox>
          </b-col>
        </b-row>

        <div class="grades-editor">
          <div
            class="d-flex justify-content-between align-items-center mb-1 flex-wrap"
            style="gap: 0.5rem"
          >
            <div>
              <h6 class="mb-0 grades-editor__title">{{ $t("Grades") }}</h6>
              <small class="text-muted">
                {{ $t("Add grade types with student counts") }}
              </small>
            </div>
            <b-button
              size="sm"
              variant="outline-primary"
              class="text-nowrap"
              @click="addGradeRow"
            >
              <feather-icon icon="PlusIcon" class="mr-50" />
              {{ $t("Add Grade") }}
            </b-button>
          </div>

          <div
            v-if="!visibleGrades.length"
            class="grades-empty text-muted text-center py-2"
          >
            {{ $t("No grades added yet") }}
          </div>

          <div
            v-for="(grade, idx) in visibleGrades"
            :key="'grade-row-' + (grade._key || grade.id || idx)"
            class="grade-row"
          >
            <div class="grade-row__sort">{{ idx + 1 }}</div>
            <b-form-input
              v-model.trim="grade.type"
              class="grade-row__type"
              :placeholder="$t('Type') + ' (A1, A, B…)'"
            />
            <b-form-input
              v-model.number="grade.count"
              class="grade-row__count"
              type="number"
              min="0"
              :placeholder="$t('Count')"
            />
            <b-button
              variant="outline-danger"
              class="grade-row__remove"
              v-b-tooltip.hover.top
              :title="$t('Remove')"
              @click="removeGradeRow(grade)"
            >
              <feather-icon icon="Trash2Icon" />
            </b-button>
          </div>

          <div class="grades-total-hint mt-1">
            <feather-icon icon="InfoIcon" size="14" class="mr-50" />
            {{ $t("No. of Students") }} =
            {{ $t("Grades") }} ({{ gradesSum }}) + {{ $t("Absent") }}
            ({{ numOrZero(form.absent) }}) =
            <strong>{{ computedStudentTotal }}</strong>
          </div>
        </div>
      </b-form>

      <template #modal-footer>
        <b-button variant="outline-secondary" @click="showModal = false">
          {{ $t("Cancel") }}
        </b-button>
        <b-button variant="primary" :disabled="saving" @click="saveItem">
          <b-spinner v-if="saving" small class="mr-50" />
          {{ isEdit ? $t("Update") : $t("Save") }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BPagination,
  BModal,
  BForm,
  BFormGroup,
  BFormCheckbox,
  BSpinner,
  BBadge,
  VBTooltip,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { mapActions } from "vuex";
import Ripple from "vue-ripple-directive";
import Swal from "sweetalert2";

let gradeKeySeq = 1;

function defaultGradeRows() {
  return ["A1", "A", "B", "C", "D", "E", "F"].map((type, i) => ({
    id: 0,
    type,
    count: 0,
    sortIndex: i + 1,
    deleted: false,
    _key: `g-${gradeKeySeq++}`,
  }));
}

export default {
  name: "YearWiseResult",
  components: {
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BPagination,
    BModal,
    BForm,
    BFormGroup,
    BFormCheckbox,
    BSpinner,
    BBadge,
    vSelect,
  },
  directives: {
    "b-tooltip": VBTooltip,
    Ripple,
  },
  data() {
    return {
      rights: {
        view: false,
        add: false,
        edit: false,
        delete: false,
      },
      viewMode: "list",
      rows: [],
      dataLoading: true,
      saving: false,
      showModal: false,
      isEdit: false,
      searchQuery: "",
      searchTimeout: null,
      statusFilter: true,
      statusOptions: [
        { text: "Active", value: true },
        { text: "Inactive", value: false },
        { text: "All", value: null },
      ],
      currentPage: 1,
      perPage: 20,
      totalCount: 0,
      pageSizeOptions: [
        { label: "10", value: 10 },
        { label: "20", value: 20 },
        { label: "50", value: 50 },
        { label: "100", value: 100 },
      ],
      fields: [
        {
          label: "S.No",
          key: "index",
          thClass: "text-center",
          tdClass: "text-center",
        },
        { key: "year", label: "Year" },
        {
          key: "noOfStudents",
          label: "Students",
          thClass: "text-center",
          tdClass: "text-center",
        },
        {
          key: "absent",
          label: "Absent",
          thClass: "text-center",
          tdClass: "text-center",
        },
        { key: "grades", label: "Grades" },
        { key: "note", label: "Note" },
        {
          key: "isActive",
          label: "Active",
          thClass: "text-center",
          tdClass: "text-center",
        },
        {
          key: "actions",
          label: "Actions",
          thClass: "text-center",
          tdClass: "text-center",
        },
      ],
      form: this.emptyForm(),
      reportLoading: false,
      reportRows: [],
      reportSummary: {
        totalYears: 0,
        totalStudents: 0,
        totalAbsent: 0,
        gradeTotals: [],
      },
      includeInactive: false,
    };
  },
  computed: {
    campusID() {
      return this.$store.state.userData.cId;
    },
    userID() {
      const u = this.$store.state.userData || {};
      return u.userID || u.localID || u.userLinkID || u.linkID || u.id || 0;
    },
    displayedRange() {
      if (this.totalCount === 0) return { start: 0, end: 0 };
      const start = (this.currentPage - 1) * this.perPage + 1;
      const end = Math.min(this.currentPage * this.perPage, this.totalCount);
      return { start, end };
    },
    visibleGrades() {
      return (this.form.grades || []).filter((g) => !g.deleted);
    },
    gradesSum() {
      return this.visibleGrades.reduce(
        (sum, g) => sum + (Number(g.count) || 0),
        0
      );
    },
    computedStudentTotal() {
      return this.gradesSum + (Number(this.form.absent) || 0);
    },
    reportSchoolName() {
      const branch = this.$store.state.currentBranch || {};
      const fromBranch =
        (branch.name && String(branch.name).trim() !== "-"
          ? String(branch.name).trim()
          : "") ||
        (branch.branch && String(branch.branch).trim() !== "-"
          ? String(branch.branch).trim()
          : "");
      if (fromBranch) return fromBranch.toUpperCase();

      const branchName = String(this.$store.state.branchName || "").trim();
      if (branchName && branchName !== "-") return branchName.toUpperCase();

      const campuses = this.$store.state.branches || [];
      const match = campuses.find((c) => c && c.id == this.campusID);
      if (match) {
        const n = String(match.name || match.branch || "").trim();
        if (n && n !== "-") return n.toUpperCase();
      }
      return "SCHOOL";
    },
    reportManagementLine() {
      const branch = this.$store.state.currentBranch || {};
      const campuses = this.$store.state.branches || [];
      const match = campuses.find((c) => c && c.id == this.campusID) || {};
      const raw =
        branch.management ||
        branch.managedBy ||
        branch.tagline ||
        branch.organization ||
        branch.orgName ||
        branch.boardName ||
        match.management ||
        match.managedBy ||
        match.tagline ||
        match.organization ||
        match.orgName ||
        "";
      const text = String(raw || "").trim();
      if (!text) {
        return "Under The Management of " + this.reportSchoolName;
      }
      if (/^under\s+the\s+management/i.test(text)) {
        return text;
      }
      return "Under The Management of " + text;
    },
    reportGradeColumns() {
      const preferred = ["A1", "A", "B", "C", "D", "E", "F"];
      const found = new Set();
      (this.reportRows || []).forEach((row) => {
        (row.grades || []).forEach((g) => {
          if (g && g.type) found.add(String(g.type).trim());
        });
      });
      (this.reportSummary.gradeTotals || []).forEach((g) => {
        if (g && g.type) found.add(String(g.type).trim());
      });

      const extras = Array.from(found)
        .filter((t) => preferred.indexOf(t) === -1)
        .sort((a, b) => a.localeCompare(b));
      return preferred.concat(extras);
    },
    sortedReportRows() {
      return [...(this.reportRows || [])].sort((a, b) =>
        String(a.year || "").localeCompare(String(b.year || ""))
      );
    },
    tableFields() {
      return (this.fields || []).filter((field) => {
        if (field.key === "actions") {
          return !!(this.rights.edit || this.rights.delete);
        }
        return true;
      });
    },
  },
  created() {
    let right = {};
    (this.$store.state.menu || []).forEach((el) => {
      (el.children || []).forEach((ch) => {
        right[ch.route] = {
          view: ch.view,
          add: ch.add,
          edit: ch.edit,
          delete: ch.delete,
        };
      });
    });
    this.$store.commit("setRights", right);

    if (!this.$store.state.rights[this.$route.name]) {
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }
    this.rights = this.$store.state.rights[this.$route.name] || {
      view: false,
      add: false,
      edit: false,
      delete: false,
    };
    if (!this.rights.view) {
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }

    this.statusOptions = [
      { text: this.$t("Active"), value: true },
      { text: this.$t("Inactive"), value: false },
      { text: this.$t("All"), value: null },
    ];
    this.loadList();
  },
  beforeDestroy() {
    if (this.searchTimeout) clearTimeout(this.searchTimeout);
  },
  methods: {
    ...mapActions({ get: "get", post: "post", delete: "delete" }),

    emptyForm() {
      return {
        id: 0,
        year: "",
        noOfStudents: 0,
        absent: 0,
        note: null,
        isActive: true,
        grades: defaultGradeRows(),
      };
    },

    skeletonBarWidth(index) {
      const widths = ["42%", "72%", "58%", "65%", "80%", "70%", "48%", "40%"];
      return widths[index % widths.length];
    },

    skeletonGridStyle(colCount) {
      const n = Math.max(Number(colCount) || 1, 1);
      return {
        gridTemplateColumns: "repeat(" + n + ", minmax(0, 1fr))",
      };
    },

    numOrZero(value) {
      if (value === null || value === undefined || value === "") return 0;
      const n = Number(value);
      return Number.isNaN(n) ? 0 : n;
    },

    sortedGrades(grades = []) {
      return [...(grades || [])].sort(
        (a, b) => (a.sortIndex || 0) - (b.sortIndex || 0)
      );
    },

    displayReportNumber(value) {
      if (value === null || value === undefined || value === "") return "-";
      return value;
    },

    displayGradeCount(item, type) {
      const grades = (item && item.grades) || [];
      const found = grades.find(
        (g) =>
          String((g && g.type) || "")
            .trim()
            .toLowerCase() === String(type || "").toLowerCase()
      );
      if (!found) return "-";
      if (
        found.count === null ||
        found.count === undefined ||
        found.count === ""
      ) {
        return "-";
      }
      const n = Number(found.count);
      if (Number.isNaN(n)) return "-";
      if (n === 0) return "-";
      return n < 10 ? "0" + n : String(n);
    },

    printReport() {
      if (!this.sortedReportRows.length) return;
      document.body.classList.add("ywr-printing");
      const cleanup = () => {
        document.body.classList.remove("ywr-printing");
        window.removeEventListener("afterprint", cleanup);
      };
      window.addEventListener("afterprint", cleanup);
      this.$nextTick(() => {
        setTimeout(() => {
          window.print();
          // Fallback if afterprint does not fire
          setTimeout(cleanup, 1000);
        }, 50);
      });
    },

    baseQuery() {
      return (
        "db=" +
        this.$store.state.userData.db +
        "&campusID=" +
        this.campusID
      );
    },

    switchView(mode) {
      this.viewMode = mode;
      if (mode === "report") {
        this.loadReport();
      }
    },

    onSearchChange() {
      if (this.searchTimeout) clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1;
        this.loadList();
      }, 400);
    },

    onFilterChange() {
      this.currentPage = 1;
      this.loadList();
    },

    onPageSizeChange() {
      this.currentPage = 1;
      this.loadList();
    },

    async loadList() {
      this.dataLoading = true;
      try {
        let url =
          this.$store.state.domain +
          "YearWiseResult/GetByPagination?" +
          this.baseQuery() +
          "&page=" +
          this.currentPage +
          "&pageSize=" +
          this.perPage;

        if (this.searchQuery && this.searchQuery.trim()) {
          url += "&search=" + encodeURIComponent(this.searchQuery.trim());
        }
        if (this.statusFilter !== null && this.statusFilter !== undefined) {
          url += "&isActive=" + this.statusFilter;
        }

        const res = await this.get({
          url,
          token: this.$store.state.userData.token,
          dashboard: true,
        });

        if (res && Array.isArray(res.data)) {
          this.rows = res.data;
          this.totalCount = res.totalCount ?? res.data.length;
        } else if (Array.isArray(res)) {
          this.rows = res;
          this.totalCount = res.length;
        } else {
          this.rows = [];
          this.totalCount = 0;
        }
      } catch (e) {
        console.error("YearWiseResult loadList", e);
        this.rows = [];
        this.totalCount = 0;
      } finally {
        this.dataLoading = false;
      }
    },

    async loadReport() {
      this.reportLoading = true;
      try {
        const url =
          this.$store.state.domain +
          "YearWiseResult/GetReport?" +
          this.baseQuery() +
          "&includeInactive=" +
          !!this.includeInactive;

        const res = await this.get({
          url,
          token: this.$store.state.userData.token,
          dashboard: true,
        });

        const data = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res)
          ? res
          : [];
        this.reportRows = data;
        this.reportSummary = res?.summary || {
          totalYears: data.length,
          totalStudents: 0,
          totalAbsent: 0,
          gradeTotals: [],
        };
      } catch (e) {
        console.error("YearWiseResult loadReport", e);
        this.reportRows = [];
        this.reportSummary = {
          totalYears: 0,
          totalStudents: 0,
          totalAbsent: 0,
          gradeTotals: [],
        };
      } finally {
        this.reportLoading = false;
      }
    },

    openAddModal() {
      if (!this.rights.add) return;
      this.isEdit = false;
      this.form = this.emptyForm();
      this.showModal = true;
    },

    async editItem(item) {
      if (!this.rights.edit) return;
      this.isEdit = true;
      this.saving = true;
      try {
        const url =
          this.$store.state.domain +
          "YearWiseResult/LoadSelected?" +
          this.baseQuery() +
          "&id=" +
          item.id;

        const res = await this.get({
          url,
          token: this.$store.state.userData.token,
          dashboard: true,
        });

        const data = res?.data || res || item;
        this.form = {
          id: data.id || 0,
          year: data.year || "",
          noOfStudents: data.noOfStudents ?? 0,
          absent: data.absent ?? 0,
          note: data.note ?? null,
          isActive: data.isActive !== false,
          grades: this.sortedGrades(data.grades || []).map((g) => ({
            id: g.id || 0,
            type: g.type || "",
            count: g.count ?? 0,
            sortIndex: g.sortIndex || 0,
            deleted: false,
            _key: `g-${gradeKeySeq++}`,
          })),
        };
        if (!this.form.grades.length) {
          this.form.grades = defaultGradeRows();
        }
        this.showModal = true;
      } catch (e) {
        console.error("YearWiseResult editItem", e);
        this.$bvToast.toast(this.$t("Could not load record."), {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
      } finally {
        this.saving = false;
      }
    },

    addGradeRow() {
      this.form.grades.push({
        id: 0,
        type: "",
        count: 0,
        sortIndex: this.visibleGrades.length + 1,
        deleted: false,
        _key: `g-${gradeKeySeq++}`,
      });
    },

    removeGradeRow(grade) {
      if (grade.id && grade.id > 0) {
        grade.deleted = true;
      } else {
        const idx = this.form.grades.indexOf(grade);
        if (idx >= 0) this.form.grades.splice(idx, 1);
      }
      this.reindexVisibleGrades();
    },

    reindexVisibleGrades() {
      this.visibleGrades.forEach((g, i) => {
        g.sortIndex = i + 1;
      });
    },

    resetForm() {
      this.form = this.emptyForm();
      this.isEdit = false;
    },

    async saveItem() {
      if (this.isEdit ? !this.rights.edit : !this.rights.add) return;

      if (!this.form.year || !String(this.form.year).trim()) {
        this.$bvToast.toast(this.$t("Please enter year."), {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
        return;
      }

      const activeGrades = this.visibleGrades.filter(
        (g) => g.type && String(g.type).trim()
      );
      if (!activeGrades.length) {
        this.$bvToast.toast(this.$t("Please add at least one grade."), {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
        return;
      }

      this.reindexVisibleGrades();

      const payload = {
        id: this.form.id || 0,
        year: String(this.form.year).trim(),
        noOfStudents: this.computedStudentTotal,
        absent: Number(this.form.absent) || 0,
        note: this.form.note ? String(this.form.note).trim() : null,
        isActive: !!this.form.isActive,
        grades: this.form.grades
          .filter((g) => (g.id && g.id > 0) || (!g.deleted && g.type))
          .map((g, i) => ({
            id: g.id || 0,
            type: String(g.type || "").trim(),
            count: Number(g.count) || 0,
            sortIndex: g.deleted ? g.sortIndex || i + 1 : g.sortIndex || i + 1,
            deleted: !!g.deleted,
          })),
      };

      this.saving = true;
      try {
        const url =
          this.$store.state.domain +
          "YearWiseResult/Save?" +
          this.baseQuery() +
          "&userID=" +
          this.userID;

        const success = await this.post({
          url,
          body: payload,
          message: this.isEdit
            ? this.$t("Yearly SSC result updated successfully")
            : this.$t("Yearly SSC result added successfully"),
          context: this,
          token: this.$store.state.userData.token,
        });

        if (success) {
          this.showModal = false;
          await this.loadList();
          if (this.viewMode === "report") await this.loadReport();
        }
      } finally {
        this.saving = false;
      }
    },

    async toggleStatus(item, checked) {
      if (!this.rights.edit) return;

      const next = !!checked;
      const prev = !!item.isActive;
      item.isActive = next;

      const url =
        this.$store.state.domain +
        "YearWiseResult/SetStatus?" +
        this.baseQuery() +
        "&id=" +
        item.id +
        "&isActive=" +
        next +
        "&userID=" +
        this.userID;

      const success = await this.post({
        url,
        body: {},
        message: next
          ? this.$t("Activated successfully")
          : this.$t("Deactivated successfully"),
        context: this,
        token: this.$store.state.userData.token,
      });

      if (!success) {
        item.isActive = prev;
      }
    },

    async deleteItem(item) {
      if (!this.rights.delete) return;

      const result = await Swal.fire({
        title: this.$t("Are you sure?"),
        text: this.$t("You won't be able to revert this!"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: this.$t("Yes, delete it!"),
      });

      if (!result.isConfirmed) return;

      const url =
        this.$store.state.domain +
        "YearWiseResult/Delete?" +
        this.baseQuery() +
        "&id=" +
        item.id +
        "&userID=" +
        this.userID;

      const success = await this.delete({
        url,
        message: this.$t("Yearly SSC result deleted successfully"),
        context: this,
        token: this.$store.state.userData.token,
      });

      if (success) {
        await this.loadList();
        if (this.viewMode === "report") await this.loadReport();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/views/myskool/planner/plannerListTable.scss";

.year-wise-result-page {
  .view-mode-btn {
    border-radius: 12px;
    font-weight: 600;
    min-height: 40px;
  }

  .year-chip {
    display: inline-flex;
    align-items: center;
    padding: 0.35rem 0.75rem;
    border-radius: 999px;
    background: linear-gradient(135deg, #f3f0ff 0%, #ebe7ff 100%);
    color: #2d1b69;
    font-weight: 700;
    font-size: 0.875rem;
  }

  .grades-cell {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
    max-width: 320px;
  }

  .grade-pill {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.2rem 0.55rem;
    border-radius: 999px;
    background: #fff;
    border: 1px solid #e0d8ff;
    color: #5e5873;
    font-size: 0.75rem;

    strong {
      color: #7367f0;
      font-weight: 700;
    }
  }

  .note-cell {
    max-width: 180px;
    color: #5e5873;
  }

  .ywr-report-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
  }

  .ywr-report-sheet {
    background: #fff;
    border: 1px solid #d8d6de;
    border-radius: 12px;
    padding: 1.75rem 1.5rem 2rem;
    overflow-x: auto;
  }

  .ywr-report-header {
    text-align: center;
    margin-bottom: 1.25rem;
    color: #000;
  }

  .ywr-school-name {
    font-size: 1.35rem;
    font-weight: 800;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    line-height: 1.3;
    color: #000;
  }

  .ywr-management-line {
    margin-top: 0.2rem;
    font-size: 0.9rem;
    font-weight: 500;
    color: #222;
  }

  .ywr-report-title {
    margin-top: 0.55rem;
    display: block;
    font-size: 1rem;
    font-weight: 700;
    text-decoration: underline;
    text-underline-offset: 3px;
    line-height: 1.4;
    color: #000;
  }

  .ywr-analysis-table {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
    min-width: 720px;
    color: #000;
    background: #fff;

    thead {
      display: table-header-group;
    }

    th,
    td {
      border: 1.5px solid #000;
      text-align: center;
      vertical-align: middle;
      padding: 0.45rem 0.35rem;
      font-size: 0.92rem;
      font-weight: 600;
      white-space: nowrap;
    }

    thead th {
      font-weight: 800;
      background: #fff;
      color: #000;
    }

    tbody td:first-child {
      font-weight: 700;
    }
  }

  .grades-total-hint {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    font-size: 0.8rem;
    color: #5e5873;
    gap: 0.15rem;

    strong {
      color: #7367f0;
      font-weight: 800;
    }
  }
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

/* Modal is teleported to body — style via modal-class */
.year-wise-result-modal {
  .grades-editor {
    margin-top: 0.25rem;
    padding: 1rem;
    border-radius: 16px;
    border: 1px solid #e0d8ff;
    background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);

    .grades-editor__title {
      color: #2d1b69;
      font-weight: 800;
    }
  }

  .grade-row {
    display: flex !important;
    flex-direction: row !important;
    flex-wrap: nowrap !important;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
    width: 100%;
  }

  .grade-row__sort {
    flex: 0 0 36px;
    width: 36px;
    height: 38px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f3f0ff;
    color: #7367f0;
    font-weight: 700;
    font-size: 0.8rem;
  }

  .grade-row__type {
    flex: 1 1 auto;
    min-width: 0;
    width: auto !important;
  }

  .grade-row__count {
    flex: 0 0 110px;
    width: 110px !important;
    max-width: 110px;
  }

  .grade-row__remove {
    flex: 0 0 42px;
    width: 42px;
    height: 38px;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .grades-total-hint {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    font-size: 0.8rem;
    color: #5e5873;
    gap: 0.15rem;

    strong {
      color: #7367f0;
      font-weight: 800;
    }
  }
}

/* Print: only the report sheet (header + full table with column titles) */
@media print {
  @page {
    size: A4 landscape;
    margin: 10mm;
  }

  html,
  body {
    background: #fff !important;
    height: auto !important;
    overflow: visible !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  body.ywr-printing * {
    visibility: hidden !important;
  }

  body.ywr-printing #ywr-report-print-area,
  body.ywr-printing #ywr-report-print-area * {
    visibility: visible !important;
  }

  body.ywr-printing #ywr-report-print-area {
    position: absolute !important;
    left: 0 !important;
    top: 0 !important;
    width: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
    border: none !important;
    border-radius: 0 !important;
    background: #fff !important;
    overflow: visible !important;
    box-shadow: none !important;
  }

  body.ywr-printing .ywr-report-header {
    display: block !important;
    text-align: center !important;
    margin: 0 0 14px !important;
    color: #000 !important;
  }

  body.ywr-printing .ywr-school-name {
    display: block !important;
    font-size: 18pt !important;
    font-weight: 800 !important;
    text-transform: uppercase !important;
    color: #000 !important;
    margin: 0 0 4px !important;
  }

  body.ywr-printing .ywr-management-line {
    display: block !important;
    font-size: 11pt !important;
    color: #000 !important;
    margin: 0 0 6px !important;
  }

  body.ywr-printing .ywr-report-title {
    display: block !important;
    font-size: 12pt !important;
    font-weight: 700 !important;
    text-decoration: underline !important;
    color: #000 !important;
    margin: 8px 0 0 !important;
  }

  body.ywr-printing .ywr-analysis-table {
    display: table !important;
    width: 100% !important;
    min-width: 0 !important;
    border-collapse: collapse !important;
    table-layout: auto !important;
    margin-top: 12px !important;
  }

  body.ywr-printing .ywr-analysis-table thead {
    display: table-header-group !important;
  }

  body.ywr-printing .ywr-analysis-table tbody {
    display: table-row-group !important;
  }

  body.ywr-printing .ywr-analysis-table tr {
    display: table-row !important;
    page-break-inside: avoid;
  }

  body.ywr-printing .ywr-analysis-table th,
  body.ywr-printing .ywr-analysis-table td {
    display: table-cell !important;
    border: 1.5px solid #000 !important;
    text-align: center !important;
    vertical-align: middle !important;
    padding: 6px 4px !important;
    font-size: 10pt !important;
    font-weight: 600 !important;
    color: #000 !important;
    background: #fff !important;
    white-space: nowrap !important;
  }

  body.ywr-printing .ywr-analysis-table thead th {
    font-weight: 800 !important;
  }
}
</style>
